import { useState } from "react";
import { Link } from "wouter";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Avatar } from "@/components/ui/avatar";
import { Loader2, Send } from "lucide-react";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Navbar } from "@/components/layout/Navbar";
import botImage from "@/assets/talking-bot.png";

// Types for our chat messages
type MessageRole = "user" | "assistant" | "system";

interface ChatMessage {
  id: string;
  role: MessageRole;
  content: string;
  timestamp: Date;
}

// Mock questions and answers for our simulated career coach
const PREDEFINED_RESPONSES: Record<string, string> = {
  "default": "I'm here to help with your career questions, especially related to the training programs at StaffAbility. Feel free to ask me about career paths, training opportunities, or support services!",
  "help": "I can help you with information about our training programs, career advice, and resources available through StaffAbility. Try asking me about specific career paths or skills you're interested in developing.",
  "programs": "StaffAbility offers training programs in several high-demand fields including: \n\n• Software Development & IT \n• Advanced Manufacturing \n• Healthcare Support \n• Logistics & Supply Chain \n• Construction Trades \n\nEach program includes hands-on training, professional certification, and direct connections to employers.",
  "coding": "Our software development program is a 9-month intensive course covering full-stack development, with specializations in web development, mobile apps, or data analysis. You'll learn languages like JavaScript, Python, and work on real-world projects with our employer partners. No prior experience is required - just dedication and willingness to learn!",
  "manufacturing": "Our advanced manufacturing program trains you on modern equipment and robotics used in today's factories. You'll learn CNC operation, quality control processes, and gain certifications valued by employers. The program takes 6 months and includes on-site training at partner facilities.",
  "logistics": "The logistics and transportation program includes training for commercial driving licenses, warehouse management systems, and supply chain operations. Many graduates find work as CDL drivers, warehouse supervisors, or logistics coordinators. The program ranges from 3-6 months depending on your specialization.",
  "support": "All StaffAbility participants receive comprehensive support services including: \n\n• Housing assistance \n• Transportation solutions \n• Mental health services \n• Case management \n• Legal advocacy \n• Financial counseling \n• Peer mentorship \n\nOur support continues for 24 months after program graduation to ensure your long-term success.",
  "requirements": "Our programs are designed specifically for individuals facing barriers to employment, especially those with justice system involvement. There are no educational prerequisites - we meet you where you are. The main requirements are commitment to the program, sobriety during training, and willingness to follow your personalized development plan.",
  "success": "Our programs have demonstrated strong outcomes in our pilot phase: \n\n• 75% employment rate for graduates \n• 32% reduction in recidivism compared to similar populations \n• Average starting wage of $19/hour \n• 68% retention rate at 1-year post-employment \n\nThese results show the effectiveness of our comprehensive, skills-first approach."
};

// Function to find the most appropriate response by checking for keywords
function findBestResponse(query: string): string {
  const normalizedQuery = query.toLowerCase();
  
  // Check for direct keyword matches
  for (const [keyword, response] of Object.entries(PREDEFINED_RESPONSES)) {
    if (keyword !== "default" && normalizedQuery.includes(keyword)) {
      return response;
    }
  }
  
  // Check for related topics
  if (normalizedQuery.includes("job") || normalizedQuery.includes("career") || normalizedQuery.includes("work")) {
    return PREDEFINED_RESPONSES["programs"];
  }
  
  if (normalizedQuery.includes("programming") || normalizedQuery.includes("developer") || normalizedQuery.includes("software") || normalizedQuery.includes("tech")) {
    return PREDEFINED_RESPONSES["coding"];
  }
  
  if (normalizedQuery.includes("factory") || normalizedQuery.includes("production")) {
    return PREDEFINED_RESPONSES["manufacturing"];
  }
  
  if (normalizedQuery.includes("truck") || normalizedQuery.includes("driving") || normalizedQuery.includes("warehouse")) {
    return PREDEFINED_RESPONSES["logistics"];
  }
  
  if (normalizedQuery.includes("house") || normalizedQuery.includes("home") || normalizedQuery.includes("counseling") || normalizedQuery.includes("therapy")) {
    return PREDEFINED_RESPONSES["support"];
  }
  
  if (normalizedQuery.includes("qualify") || normalizedQuery.includes("eligible") || normalizedQuery.includes("join")) {
    return PREDEFINED_RESPONSES["requirements"];
  }
  
  if (normalizedQuery.includes("results") || normalizedQuery.includes("statistics") || normalizedQuery.includes("effective")) {
    return PREDEFINED_RESPONSES["success"];
  }
  
  // Default fallback
  return PREDEFINED_RESPONSES["default"];
}

export default function CareerCoachPage() {
  const [messages, setMessages] = useState<ChatMessage[]>([
    {
      id: "welcome",
      role: "assistant",
      content: "Hello! I'm your StaffAbility Career Coach assistant. How can I help you explore career opportunities today?",
      timestamp: new Date()
    }
  ]);
  
  const [inputValue, setInputValue] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  
  // Function to simulate the AI responding with a short delay
  const simulateResponse = (query: string) => {
    setIsTyping(true);
    
    // Simulate thinking time
    setTimeout(() => {
      const responseText = findBestResponse(query);
      
      setMessages(prev => [...prev, {
        id: Date.now().toString(),
        role: "assistant",
        content: responseText,
        timestamp: new Date()
      }]);
      
      setIsTyping(false);
    }, 1000 + Math.random() * 2000); // Random delay between 1-3 seconds
  };
  
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (inputValue.trim() === "") return;
    
    // Add user message
    const userMessage: ChatMessage = {
      id: Date.now().toString(),
      role: "user",
      content: inputValue,
      timestamp: new Date()
    };
    
    setMessages(prev => [...prev, userMessage]);
    simulateResponse(inputValue);
    setInputValue("");
  };
  
  return (
    <>
      <Navbar />
      <div className="container mx-auto p-2 sm:p-4 max-w-5xl pt-16 sm:pt-20">
        <Card className="w-full shadow-2xl border-blue-100 overflow-hidden rounded-xl">
          <CardHeader className="bg-gradient-to-r from-blue-600 to-blue-700 text-white relative py-3 sm:py-6">
            <div className="absolute inset-0 overflow-hidden opacity-20">
              {Array.from({ length: 20 }).map((_, i) => (
                <div 
                  key={i} 
                  className="absolute bg-white/30 rounded-full"
                  style={{
                    width: `${Math.random() * 8 + 4}px`,
                    height: `${Math.random() * 8 + 4}px`,
                    top: `${Math.random() * 100}%`,
                    left: `${Math.random() * 100}%`,
                    opacity: Math.random() * 0.8
                  }}
                ></div>
              ))}
            </div>
            <CardTitle className="flex items-center gap-2 sm:gap-4 relative z-10">
              <Avatar className="h-10 w-10 sm:h-14 sm:w-14 bg-white text-blue-600 ring-2 ring-blue-300 shadow-lg flex items-center justify-center">
                {/* Message icon */}
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-6 h-6 sm:w-8 sm:h-8">
                  <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
                </svg>
              </Avatar>
              <div>
                <div className="text-lg sm:text-2xl font-bold">StaffAbility Coach</div>
                <div className="flex items-center text-xs sm:text-sm text-blue-100">
                  <div className="h-2 w-2 sm:h-2.5 sm:w-2.5 rounded-full bg-green-400 mr-1 sm:mr-2 animate-pulse"></div>
                  Online and ready to assist
                </div>
              </div>
            </CardTitle>
            <CardDescription className="text-blue-100 relative z-10 pl-12 sm:pl-[72px] mt-1 text-xs sm:text-sm md:text-base">
              Ask about training programs, career paths, or support services
            </CardDescription>
          </CardHeader>
          
          <CardContent className="p-2 sm:p-4 md:p-6 h-[35vh] sm:h-[40vh] md:h-[50vh] overflow-y-auto flex flex-col gap-2 sm:gap-4 bg-blue-50/30">
            {messages.map((message) => (
              <div 
                key={message.id} 
                className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'} gap-1.5 sm:gap-3 items-start`}
              >
                {message.role === 'assistant' && (
                  <Avatar className="mt-1 h-8 w-8 sm:h-10 sm:w-10 bg-white text-blue-600 flex items-center justify-center ring-2 ring-white shadow-md p-0 overflow-hidden">
                    <img src={botImage} alt="AI Assistant" className="w-full h-full object-contain" />
                  </Avatar>
                )}
                
                <div 
                  className={`max-w-[85%] rounded-xl p-2.5 sm:p-4 shadow-md ${
                    message.role === 'user' 
                      ? 'bg-gradient-to-br from-blue-600 to-blue-700 text-white ml-auto border border-blue-500/20' 
                      : 'bg-white text-slate-800 border border-blue-100'
                  }`}
                >
                  <div className="whitespace-pre-line text-xs sm:text-sm md:text-base leading-relaxed">{message.content}</div>
                  <div className={`text-[10px] sm:text-xs mt-1 sm:mt-2 flex items-center ${message.role === 'user' ? 'text-blue-200 justify-end' : 'text-slate-400'}`}>
                    {message.role === 'assistant' && (
                      <svg className="w-2.5 h-2.5 sm:w-3 sm:h-3 mr-1" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M12 6V12L16 14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    )}
                    {message.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </div>
                </div>
                
                {message.role === 'user' && (
                  <Avatar className="mt-1 h-8 w-8 sm:h-10 sm:w-10 bg-blue-600 text-white ring-2 ring-white shadow-md">
                    <span className="font-semibold text-xs sm:text-sm">You</span>
                  </Avatar>
                )}
              </div>
            ))}
            
            {isTyping && (
              <div className="flex justify-start gap-1.5 sm:gap-3 items-start">
                <Avatar className="mt-1 h-8 w-8 sm:h-10 sm:w-10 bg-white text-blue-600 flex items-center justify-center ring-2 ring-white shadow-md p-0 overflow-hidden">
                  <img src={botImage} alt="AI Assistant" className="w-full h-full object-contain" />
                </Avatar>
                <div className="bg-white rounded-xl p-2.5 sm:p-4 text-slate-800 shadow-md border border-blue-100">
                  <div className="flex gap-1 sm:gap-2 items-center">
                    <div className="flex space-x-0.5 sm:space-x-1">
                      <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-blue-600 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                      <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-blue-600 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                      <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-blue-600 rounded-full animate-bounce" style={{ animationDelay: '600ms' }}></div>
                    </div>
                    <span className="text-xs sm:text-sm text-slate-500 ml-1">Typing...</span>
                  </div>
                </div>
              </div>
            )}
          </CardContent>
          
          <CardFooter className="p-3 sm:p-4 border-t bg-white sticky bottom-0">
            <form onSubmit={handleSubmit} className="w-full flex gap-2 sm:gap-3">
              <Input
                placeholder="Ask a question..."
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                className="flex-1 border-blue-200 focus:border-blue-400 shadow-sm py-3 sm:py-5 text-sm sm:text-base"
                disabled={isTyping}
              />
              <Button 
                type="submit" 
                disabled={isTyping || inputValue.trim() === ""}
                className="bg-blue-600 hover:bg-blue-700 text-white px-3 sm:px-5"
              >
                {isTyping ? <Loader2 className="h-4 w-4 sm:h-5 sm:w-5 animate-spin" /> : <Send className="h-4 w-4 sm:h-5 sm:w-5" />}
              </Button>
            </form>
          </CardFooter>
        </Card>
        
        <div className="mt-6 sm:mt-10 bg-gradient-to-br from-blue-50 to-blue-100 border border-blue-200 rounded-xl p-4 sm:p-6 shadow-lg">
          <h3 className="font-semibold text-blue-900 mb-3 sm:mb-5 text-lg sm:text-xl flex items-center">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4 sm:w-5 sm:h-5 mr-1.5 sm:mr-2 text-blue-600">
              <circle cx="12" cy="12" r="10"></circle>
              <path d="M12 8v4l3 3"></path>
            </svg>
            Popular questions to try:
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2 sm:gap-4">
            {[
              {text: 'Training Programs', icon: '📚'},
              {text: 'Software Development', icon: '💻'},
              {text: 'Logistics Careers', icon: '🚚'},
              {text: 'Support Services', icon: '🏠'},
              {text: 'Entry Requirements', icon: '📝'},
              {text: 'Success Stories', icon: '🌟'}
            ].map((suggestion) => (
              <Button 
                key={suggestion.text} 
                variant="outline" 
                className="bg-white shadow-md border-blue-200 hover:bg-blue-50 hover:border-blue-300 justify-start p-2.5 sm:p-4 h-auto text-xs sm:text-sm md:text-base"
                onClick={() => {
                  const userMessage: ChatMessage = {
                    id: Date.now().toString(),
                    role: "user",
                    content: `Tell me about ${suggestion.text.toLowerCase()}`,
                    timestamp: new Date()
                  };
                  
                  setMessages(prev => [...prev, userMessage]);
                  simulateResponse(suggestion.text.toLowerCase());
                }}
              >
                <span className="mr-2 sm:mr-3 text-base sm:text-xl">{suggestion.icon}</span>
                <span>{suggestion.text}</span>
              </Button>
            ))}
          </div>
          
          <div className="mt-4 sm:mt-8 border-t border-blue-200 pt-3 sm:pt-5 text-center">
            <p className="text-xs sm:text-sm text-slate-600 mb-1 sm:mb-2">This AI assistant simulates responses based on predefined content about StaffAbility's programs.</p>
            <p className="text-[10px] sm:text-xs text-slate-500">For detailed inquiries, please <a href="/contact" className="text-blue-600 hover:underline font-medium">contact us directly</a>.</p>
          </div>
        </div>
      </div>
    </>
  );
}